import React, { useEffect, useState } from 'react';
import { api, endpoints } from '../apiConfig';
import '../styles/UnifiedSearch.css';
import '../styles/ExploreFeed.css';
import LikeButton from './social/LikeButton';
import CommentSection from './social/CommentSection';
import ShareButton from './social/ShareButton';
import { connectSocket, joinItemRoom, leaveItemRoom } from '../utils/socket';
import { useAuth } from '../contexts/AuthContext';
import { formatBuyerDisplayPrice } from '../utils/pricing';
import { exhibitionPath, exhibitionPublicUrl } from '../utils/exhibitionLinks';

// Utility to strip HTML tags
const stripHtml = (html) => html ? html.replace(/<[^>]+>/g, '') : '';

// Mapping of article titles to Shopify CDN image URLs
const articleImages = {
  "Emerging Artists to Watch": "https://cdn.shopify.com/s/files/1/0438/8087/4152/files/Emerging_Artists_to_Watch.png?v=1746017862",
  "The Importance of Provenance in Art": "https://cdn.shopify.com/s/files/1/0438/8087/4152/files/The_Importance_of_Provenance_in_Art.png?v=1746017862",
  "Tips for New Art Collectors": "https://cdn.shopify.com/s/files/1/0438/8087/4152/files/Tips_for_New_Art_Collectors.png?v=1746017862",
  "The Future of Digital Art": "https://cdn.shopify.com/s/files/1/0438/8087/4152/files/The_Future_of_Digital_Art.png?v=1746017862",
  "The Basics of Art Collecting": "https://cdn.shopify.com/s/files/1/0438/8087/4152/files/The_Basics_of_Art_Collecting.png?v=1746017862",
  "Contemporary vs. Classical Art Collecting": "https://cdn.shopify.com/s/files/1/0438/8087/4152/files/Contemporary_vs._Classical_Art_Collecting.png?v=1746017862"
};

const getArticleImage = (title, fallback) => articleImages[title] || fallback;

const slugify = (value) => (value || '')
  .toString()
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .substring(0, 60);

// Each seller's works live in a Shopify collection named artist-<slug of name>.
const galleryUrl = (gallery) => {
  if (gallery.available_count > 0) {
    return `https://artemerge.org/collections/artist-${slugify(gallery.title)}`;
  }
  if (gallery.website) {
    return /^https?:\/\//i.test(gallery.website) ? gallery.website : `https://${gallery.website}`;
  }
  return null;
};

const initials = (name) => (name || '?')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((word) => word[0].toUpperCase())
  .join('');

const ExploreUnifiedSearch = ({ searchQuery, filter = 'all' }) => {
  const { currentUser } = useAuth();
  const [feedResults, setFeedResults] = useState([]);
  const [shopifyResults, setShopifyResults] = useState([]);
  const [aiResults, setAiResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [interactions, setInteractions] = useState({}); // Store interaction data

  // Initialize Socket.io when component mounts
  useEffect(() => {
    if (currentUser) {
      connectSocket();
    }
  }, [currentUser]);

  useEffect(() => {
    let isMounted = true;
    const fetchFeed = async () => {
      setLoading(true);
      setError(null);
      try {
        const feedRes = await api.get(endpoints.explore.feed, {
          params: { filter, search: searchQuery }
        });
        if (!isMounted) return;
        
        const feedItems = feedRes.data.items || [];
        setFeedResults(feedItems);
        
        // Load interaction data for each feed item
        await loadInteractionsForItems(feedItems);
      } catch (err) {
        if (!isMounted) return;
        setError('Failed to fetch feed results.');
        setFeedResults([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchFeed();
    // Clear Shopify/AI results if search is empty
    if (!searchQuery || searchQuery.trim().length === 0) {
      setShopifyResults([]);
      setAiResults([]);
    }
    return () => { isMounted = false; };
  }, [searchQuery, filter]);

  useEffect(() => {
    let isMounted = true;
    const fetchShopifyAI = async () => {
      if (!searchQuery || searchQuery.trim().length === 0) return;
      setLoading(true);
      setError(null);
      try {
        const shopifyAIRes = await api.get(`/search/search?query=${encodeURIComponent(searchQuery)}`);
        if (!isMounted) return;
        setShopifyResults(shopifyAIRes.data.shopify_results || []);
        setAiResults(shopifyAIRes.data.ai_results || []);
      } catch (err) {
        if (!isMounted) return;
        setError('Failed to fetch Shopify/AI search results.');
        setShopifyResults([]);
        setAiResults([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchShopifyAI();
    return () => { isMounted = false; };
  }, [searchQuery]);

  // Load interaction data for feed items
  const loadInteractionsForItems = async (items) => {
    try {
      const interactionPromises = items.map(async (item) => {
        try {
          const response = await api.get(`/social/interactions/${item.type}/${item.id}`);
          return { [item.id]: response.data };
        } catch (error) {
          console.error(`Error loading interactions for ${item.type} ${item.id}:`, error);
          return { [item.id]: { likes_count: 0, comments_count: 0, shares_count: 0, liked: false, saved: false } };
        }
      });

      const interactionResults = await Promise.all(interactionPromises);
      const interactionData = interactionResults.reduce((acc, curr) => ({ ...acc, ...curr }), {});
      setInteractions(interactionData);
    } catch (error) {
      console.error('Error loading interactions:', error);
    }
  };

  // Join/leave Socket.io rooms when items change
  useEffect(() => {
    feedResults.forEach(item => {
      joinItemRoom(item.id, item.type);
    });

    return () => {
      feedResults.forEach(item => {
        leaveItemRoom(item.id, item.type);
      });
    };
  }, [feedResults]);

  // Feed card component with expandable description and product links
  const FeedCard = ({ item, itemInteractions }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const fullText = item.type === 'article' && item.summary
      ? item.summary
      : stripHtml(item.description || '');
    const previewLimit = 160; // show a bit more by default
    const displayText = isExpanded ? fullText : fullText.slice(0, previewLimit);

    const imageUrl = item.type === 'article'
      ? getArticleImage(item.title, item.image_url || '/default-article.png')
      : item.image_url;

    const isGallery = item.type === 'gallery';
    const isExhibition = item.type === 'exhibition';
    const productUrl = isGallery
      ? galleryUrl(item)
      : isExhibition
        ? exhibitionPath(item)
        : item.shopify_handle
        ? `https://artemerge.org/products/${item.shopify_handle}`
        : item.shopify_product_id
          ? `https://artemerge.org/products/${item.shopify_product_id}`
          : null;

    const Title = () => (
      productUrl ? (
        <a href={productUrl} target="_blank" rel="noopener noreferrer">
          {item.title}
        </a>
      ) : (
        <>{item.title}</>
      )
    );

    const picture = imageUrl
      ? <img src={imageUrl} alt={item.title} loading="lazy" />
      : <span className="result-image__initials" aria-hidden="true">{initials(item.title)}</span>;

    const Image = () => (
      productUrl ? (
        <a href={productUrl} target="_blank" rel="noopener noreferrer">
          {picture}
        </a>
      ) : picture
    );

    return (
      <div className="result-card feed-card">
        <div className="result-image">
          <Image />
        </div>
        <div className="result-content">
          <h3><Title /></h3>
          {item.type && <div className="result-type">{item.type.charAt(0).toUpperCase() + item.type.slice(1)}</div>}
          {item.type === 'artwork' && (
            <div className="result-meta">
              {item.artist_name && <span className="artist-name">{item.artist_name}</span>}
              {item.product_type && <span className="dot"> · </span>}
              {item.product_type && <span className="medium">{item.product_type}</span>}
            </div>
          )}
          {isGallery && (
            <div className="result-meta">
              {[
                item.location,
                item.available_count > 0
                  ? `${item.available_count} available work${item.available_count === 1 ? '' : 's'}`
                  : null
              ].filter(Boolean).join(' · ')}
            </div>
          )}
          {item.price && <p className="price">{formatBuyerDisplayPrice(item.price, { estimatedShipping: item.estimated_shipping_price })}</p>}
          {fullText && (
            <p className="description">
              {displayText}
              {!isExpanded && fullText.length > previewLimit ? '… ' : ' '}
              {fullText.length > previewLimit && (
                <button
                  type="button"
                  className="expand-btn"
                  onClick={() => setIsExpanded(prev => !prev)}
                >
                  {isExpanded ? 'Show less' : 'Read more'}
                </button>
              )}
            </p>
          )}

          {/* Social Interactions */}
          <div className="feed-card__social">
            <div className="feed-card__social-actions">
              <LikeButton
                itemId={item.id}
                itemType={item.type}
                initialLiked={itemInteractions.liked}
                initialCount={itemInteractions.likes_count}
                size="small"
                onLikeChange={(liked, count) => {
                  setInteractions(prev => ({
                    ...prev,
                    [item.id]: { ...prev[item.id], liked, likes_count: count }
                  }));
                }}
              />

              <CommentSection
                itemId={item.id}
                itemType={item.type}
                initialCount={itemInteractions.comments_count}
                onToggle={() => {}}
              />

              <ShareButton
                itemId={item.id}
                itemType={item.type}
                title={item.title}
                description={fullText.slice(0, 100)}
                imageUrl={item.image_url}
                url={isExhibition ? exhibitionPublicUrl(item) : undefined}
                size="small"
              />
            </div>
          </div>

          {productUrl && (
            <div className="purchase-cta">
              <a href={productUrl} target="_blank" rel="noopener noreferrer" className="purchase-link">
                {isGallery
                  ? (item.available_count > 0 ? 'View gallery works' : 'Visit website')
                  : isExhibition ? 'View exhibition' : 'View on Shopify'}
              </a>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderShopifyCard = (result) => (
    <div key={result.id} className="result-card">
      <div className="result-image">
        <img 
          src={result.image_url || '/default-artwork.png'} 
          alt={result.title} 
          loading="lazy"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://via.placeholder.com/300x300?text=No+Image';
          }}
        />
      </div>
      <div className="result-content">
        <h3>{result.title}</h3>
        {result.price && <p className="price">${Math.round(parseFloat(result.price)).toLocaleString()}</p>}
      </div>
    </div>
  );

  const renderAICard = (result) => (
    <div key={result.id} className="result-card">
      <div className="result-image">
        <img 
          src={result.image_url || '/default-artwork.png'} 
          alt={result.title} 
          loading="lazy"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://via.placeholder.com/300x300?text=No+Image';
          }}
        />
      </div>
      <div className="result-content">
        <h3>{result.title}</h3>
        {result.price && <p className="price">{formatBuyerDisplayPrice(result.price, { estimatedShipping: result.estimated_shipping_price })}</p>}
        <div className="relevance-badge">{Math.round(result.score * 100)}% Match</div>
      </div>
    </div>
  );

  return (
    <div className="unified-search">
      {error && <div className="error-message">{error}</div>}
      {loading ? (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <span>Searching...</span>
        </div>
      ) : (
        <div className="search-results">
          {/* Feed Results */}
          {feedResults.length > 0 && (
            <section className="results-section feed-results">
              <h2>Feed Results</h2>
              <div className="vertical-feed">
                {feedResults.map(item => (
                  <FeedCard key={item.id} item={item} itemInteractions={interactions[item.id] || { likes_count: 0, comments_count: 0, shares_count: 0, liked: false, saved: false }} />
                ))}
              </div>
            </section>
          )}
          {/* Shopify Results */}
          {searchQuery && shopifyResults.length > 0 && (
            <section className="results-section shopify-results">
              <h2>Shopify Results</h2>
              <div className="results-grid">
                {shopifyResults.map(renderShopifyCard)}
              </div>
            </section>
          )}
          {/* AI Results */}
          {searchQuery && aiResults.length > 0 && (
            <section className="results-section ai-results">
              <h2>AI-Powered Recommendations</h2>
              <div className="results-grid">
                {aiResults.map(renderAICard)}
              </div>
            </section>
          )}
          {/* No Results */}
          {!loading && feedResults.length === 0 && (!searchQuery || (shopifyResults.length === 0 && aiResults.length === 0)) && (
            <div className="no-results">
              {searchQuery ? `No results found for "${searchQuery}"` : 'No feed items found.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExploreUnifiedSearch; 